import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import { toastMessage } from "shared/components/common/toast";
import SkeletonLoader from "shared/loaders/skeleton";
import styles from "./style.module.scss";
import {
  DeleteProductMakeService,
  GetAllBrandsService,
  GetBrandByCategoryService,
} from "shared/services/productBrands";
import CustomToolTip from "shared/components/common/customToolTip";
import { Icons } from "assets";
import ConfirmationModal from "shared/modal/confirmation";
import { handleErrors } from "shared/utils/helper";

interface BrandsData {
  id: number;
  title: string;
  type: string;
}

interface BrandListingsProps {
  setEditMode: (value: boolean) => void;
  setSelectedBrand: (value: any) => void;
  activeTab: string;
  selectedBrand: any;
}

const BrandListings = ({
  setEditMode,
  setSelectedBrand,
  activeTab,
  selectedBrand,
}: BrandListingsProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [brands, setBrands] = useState<BrandsData[]>([]);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  const handleShowConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const tableHeads = [
    { title: "#", key: "sr" },
    { title: "Title", key: "title" },
    { title: "Type", key: "type" },
    { title: "Action", key: "type" },
  ];

  const handleGetAllBrands = async (categ: string) => {
    try {
      setLoading(true);
      const response = await GetBrandByCategoryService(categ);
      setBrands(response.data.data[activeTab] || []);
    } catch (error) {
      console.error("Error fetching brands:", error);
      toastMessage("error", "Failed to fetch brands.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBrand = async () => {
    if (!selectedBrand) return;
    setDeleteLoading(true);
    DeleteProductMakeService(selectedBrand?.id)
      .then(({ data, status }) => {
        if (status) {
          toastMessage("success", "Make deleted successfully.");
          handleGetAllBrands(activeTab);
          handleCloseConfirmationModal();
        }
      })
      .catch((err) => {
        handleErrors(err);
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  useEffect(() => {
    handleGetAllBrands(activeTab);
  }, [activeTab]);

  return (
    <div className={styles.sectionContainer}>
      <div className={classNames(styles.tableWrapper, "relative")}>
        <CustomTable
          heads={tableHeads}
          title="Brands List"
          isEmpty={!loading && !brands.length}
        >
          {loading
            ? Array.from({ length: 2 }).map((_, index) => (
                <TableRowLoader key={index} />
              ))
            : brands.map((data, idx) => (
                <tr key={data.id}>
                  <td>{idx + 1}</td>
                  <td>{data.title}</td>
                  <td>{data.type}</td>
                  <td>
                    <div className="flex items-center justify-center gap-2">
                      <CustomToolTip label="Update">
                        <button
                          onClick={() => {
                            setEditMode(true);
                            setSelectedBrand(data);
                          }}
                        >
                          <Icons.UpdateBlog />
                        </button>
                      </CustomToolTip>
                      <CustomToolTip label="Delete">
                        <button
                          onClick={() => {
                            setSelectedBrand(data);
                            handleShowConfirmationModal();
                          }}
                        >
                          <Icons.TrashOutline />
                        </button>
                      </CustomToolTip>
                    </div>
                  </td>
                </tr>
              ))}
        </CustomTable>
      </div>
      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={handleCloseConfirmationModal}
        handleSubmit={handleDeleteBrand}
        loading={deleteLoading}
      />
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 4 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default BrandListings;
